import { Fragment, useId, useMemo, useRef, useState } from 'react';
import {
  apiLoadErrorText,
  useListEconChronumPositionsQuery,
  type ChronumPayrollCategory,
  type ChronumPosition,
} from '@shared/api';
import { IconArrowRight, IconSearch } from '@shared/ui/icons';
import { ScrollRegion, VisuallyHidden } from '@entities/fleet';
import {
  categoryTitle,
  countUnmapped,
  filterPositions,
  formatDateTime,
  formatInt,
  originLabel,
  plural,
  sortPositions,
  typo,
} from '@entities/chronum';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { usePositionSaver } from '../usePositionSaver';
import { ArrowLink, EmptyBox, ListSkeleton, SectionNote } from '../ui';
import {
  NothingFound,
  PanelRoot,
  Search,
  SearchInput,
  Spacer,
  Summary,
  Table,
  Toggle,
  Toolbar,
} from './styles';
import { COLUMNS } from './styles';
import { PositionRow } from './PositionRow';
import { PositionDetails } from './PositionDetails';

export interface Props {
  categories: readonly ChronumPayrollCategory[];
  canWrite: boolean;
  unmappedOnly: boolean;
  onUnmappedOnly: (on: boolean) => void;
}

/**
 * «Должности»: категория ФОТ каждой должности Хронума, комментарий, журнал
 * правок и группа механизаторов.
 */
export function PositionsPanel({ categories, canWrite, unmappedOnly, onUnmappedOnly }: Props) {
  const query = useListEconChronumPositionsQuery();
  const detailsBase = useId();
  const [search, setSearch] = useState('');
  // Строки, которым при включённом «без категории» уже выбрали категорию: они
  // остаются на месте до смены фильтра или поиска.
  const [keep, setKeep] = useState<ReadonlySet<string>>(() => new Set());
  const [openKey, setOpenKey] = useState<string | null>(null);
  const saver = usePositionSaver((c) => (c ? categoryTitle(c, categories) : 'без категории'));
  const rowIds = useRef(new Map<string, string>());

  const items = query.data?.items;
  const sorted = useMemo(() => sortPositions(items ?? []), [items]);
  const visible = useMemo(
    () => filterPositions(sorted, { query: search, unmappedOnly, keep }),
    [sorted, search, unmappedOnly, keep],
  );
  const unmapped = countUnmapped(items ?? []);
  const snapshot = query.data?.snapshot ?? null;

  function detailsId(key: string): string {
    let id = rowIds.current.get(key);
    if (!id) {
      id = `${detailsBase}-${rowIds.current.size}`;
      rowIds.current.set(key, id);
    }
    return id;
  }

  function onSearch(value: string) {
    setSearch(value);
    setKeep(new Set());
  }

  function onToggleFilter() {
    setKeep(new Set());
    onUnmappedOnly(!unmappedOnly);
  }

  function onChoose(position: ChronumPosition, category: string | null) {
    if (unmappedOnly) setKeep((prev) => new Set(prev).add(position.key));
    saver.choose(position, category);
  }

  if (query.isError && !query.data) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(
            query.error,
            'Не удалось загрузить должности Хронума.',
            'Нет доступа к должностям Хронума.',
          )}
        </span>
        <TextButton type="button" onClick={() => void query.refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (query.isLoading || !query.data) {
    return <ListSkeleton rows={8} label="Загрузка должностей" />;
  }
  if (sorted.length === 0) {
    return (
      <EmptyBox>
        <h3>Должностей пока нет</h3>
        <p>
          {typo(
            snapshot
              ? 'В последнем списке Хронума нет должностей у компаний ключа Экономиста.'
              : 'Хронум ещё не отдаёт список должностей — эту выгрузку делают на стороне Хронума. Когда она появится, должности придут с первой же загрузкой: по кнопке «Обновить» или по расписанию.',
          )}
        </p>
      </EmptyBox>
    );
  }

  return (
    <PanelRoot aria-busy={query.isFetching}>
      <Toolbar role="search" aria-label="Поиск должностей">
        <Search>
          <IconSearch size={16} />
          <SearchInput
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Должность, компания или комментарий"
            aria-label="Поиск по должности, компании и комментарию"
            autoComplete="off"
            spellCheck={false}
          />
        </Search>
        {(unmapped > 0 || unmappedOnly) && (
          <Toggle type="button" aria-pressed={unmappedOnly} onClick={onToggleFilter}>
            Только без категории · {formatInt(unmapped)}
          </Toggle>
        )}
        <Spacer />
        <ArrowLink to="/agents/econ/fleet?tab=groups">
          Группы — в разделе Техника → Группы механизаторов
          <IconArrowRight size={14} />
        </ArrowLink>
      </Toolbar>

      <Summary aria-live="polite">
        {search || unmappedOnly ? 'Найдено: ' : ''}
        <strong>
          {formatInt(visible.length)}{' '}
          {plural(visible.length, ['должность', 'должности', 'должностей'])}
        </strong>
        {' · '}
        {unmapped > 0 ? `без категории ФОТ — ${formatInt(unmapped)}` : 'у всех есть категория ФОТ'}
      </Summary>
      <SectionNote>
        {snapshot
          ? typo(
              `Список Хронума от ${formatDateTime(snapshot.fetched_at)} (${originLabel(snapshot)}). Одинаковые должности разных компаний склеены в одну — категория действует для всех.`,
            )
          : typo(
              'Хронум ещё не отдаёт список должностей — показаны те, которым категория уже выбрана.',
            )}
        {canWrite &&
          typo(
            ' Категория сохраняется сразу. Если коллега успел изменить должность раньше вас, правка не перетрёт его — появится «Перечитать».',
          )}
      </SectionNote>

      {visible.length === 0 ? (
        <NothingFound>
          Ничего не нашлось.{' '}
          <TextButton
            type="button"
            onClick={() => {
              onSearch('');
              if (unmappedOnly) onUnmappedOnly(false);
            }}
          >
            Сбросить
          </TextButton>
        </NothingFound>
      ) : (
        <ScrollRegion label="Должности Хронума — таблица">
          <Table>
            <thead>
              <tr>
                <th scope="col">Должность</th>
                <th scope="col">Компании</th>
                <th scope="col" className="num">
                  Работников
                </th>
                <th scope="col">Категория ФОТ</th>
                <th scope="col">Группа механизаторов</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const open = openKey === p.key;
                const id = detailsId(p.key);
                return (
                  <Fragment key={p.key}>
                    <PositionRow
                      position={p}
                      categories={categories}
                      canWrite={canWrite}
                      save={saver.state(p.key)}
                      open={open}
                      detailsId={id}
                      onChoose={onChoose}
                      onRetry={saver.retry}
                      onReread={saver.reread}
                      onToggle={(key) => setOpenKey((cur) => (cur === key ? null : key))}
                    />
                    {open && (
                      <tr className="details" id={id}>
                        <td colSpan={COLUMNS}>
                          <PositionDetails
                            position={p}
                            categories={categories}
                            canWrite={canWrite}
                            saver={saver}
                            listUpdatedAt={query.fulfilledTimeStamp ?? 0}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </ScrollRegion>
      )}
      <VisuallyHidden role="status" aria-live="polite">
        {saver.announcement}
      </VisuallyHidden>
    </PanelRoot>
  );
}
